import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useState, useEffect } from "react";
import { differenceInMonths, parseISO } from "date-fns";
import { calculateRemainingBalance } from "@/components/admin/tvm/calculations";
import type { TVMInputs } from "@/components/admin/tvm/types";

interface Deal {
  date_of_sale?: string;
  first_payment_date?: string;
  first_payment_to_me?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  has_balloon_payment: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  current_balance?: number;
  payments_remaining?: number;
  estimated_property_value?: number;
  offer_price?: number;
  offer_date?: string;
  investor_quote?: number;
}

interface FinancialTabProps {
  formData: Deal;
  onChange: (field: keyof Deal, value: any) => void;
}

export function FinancialTab({ formData, onChange }: FinancialTabProps) {
  const [paymentsMade, setPaymentsMade] = useState<number>(0);
  // Initialize override to true if there's already a saved balance (preserve user's value)
  const [manualBalanceOverride, setManualBalanceOverride] = useState<boolean>(
    () => formData.current_balance !== undefined && formData.current_balance !== null
  );

  // Calculate payments made (from first_payment_date to first_payment_to_me)
  useEffect(() => {
    if (formData.first_payment_date && formData.first_payment_to_me) {
      try {
        const startDate = parseISO(formData.first_payment_date);
        const endDate = parseISO(formData.first_payment_to_me);
        const months = differenceInMonths(endDate, startDate);
        setPaymentsMade(Math.max(0, months));
      } catch (e) {
        setPaymentsMade(0);
      }
    } else {
      setPaymentsMade(0);
    }
  }, [formData.first_payment_date, formData.first_payment_to_me]);

  // Auto-calculate current balance when first_payment_to_me changes (only if not manually overridden)
  // Note: Payments Remaining is now manual to avoid confusion about what "Term" means
  useEffect(() => {
    if (manualBalanceOverride) return; // Skip if user has manually entered a value
    
    if (
      formData.first_payment_to_me &&
      formData.beginning_balance &&
      formData.interest_rate !== undefined &&
      formData.payment_amount &&
      paymentsMade > 0
    ) {
      try {
        const tvmInputs: TVMInputs = {
          presentValue: formData.beginning_balance,
          futureValue: 0,
          payment: formData.payment_amount,
          interestRate: formData.interest_rate,
          periods: paymentsMade, // periods represents payments made
          paymentsPerYear: 12,
          balloonPayment: 0,
          paymentsUntilBalloon: 0,
        };

        const remainingBalance = calculateRemainingBalance(tvmInputs);

        onChange('current_balance', Math.max(0, remainingBalance));
        // Removed auto-calculation of payments_remaining to allow manual entry
      } catch (e) {
        console.error("Error calculating balance:", e);
      }
    }
  }, [
    formData.first_payment_to_me,
    formData.beginning_balance,
    formData.interest_rate,
    formData.payment_amount,
    paymentsMade,
    manualBalanceOverride,
    onChange,
  ]);

  // Handle manual balance change
  const handleBalanceChange = (value: number | undefined) => {
    setManualBalanceOverride(true);
    onChange('current_balance', value);
  };

  // Reset manual override when recalculate is requested
  const handleRecalculateBalance = () => {
    setManualBalanceOverride(false);
  };
  return (
    <div className="space-y-6">
      {/* Original Contract Terms */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Original Contract Terms</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="date_of_sale">Date of Sale</Label>
            <Input
              id="date_of_sale"
              type="date"
              value={formData.date_of_sale || ''}
              onChange={(e) => onChange('date_of_sale', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="first_payment_date">First Payment</Label>
            <Input
              id="first_payment_date"
              type="date"
              value={formData.first_payment_date || ''}
              onChange={(e) => onChange('first_payment_date', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sales_price">Sales Price</Label>
            <Input
              id="sales_price"
              type="number"
              step="0.01"
              value={formData.sales_price || ''}
              onChange={(e) => onChange('sales_price', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="down_payment_amount">Down Payment</Label>
            <Input
              id="down_payment_amount"
              type="number"
              step="0.01"
              value={formData.down_payment_amount || ''}
              onChange={(e) => onChange('down_payment_amount', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="beginning_balance">Beginning Balance</Label>
            <Input
              id="beginning_balance"
              type="number"
              step="0.01"
              value={formData.beginning_balance || ''}
              onChange={(e) => onChange('beginning_balance', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="interest_rate">Interest Rate (%)</Label>
            <Input
              id="interest_rate"
              type="number"
              step="0.01"
              value={formData.interest_rate || ''}
              onChange={(e) => onChange('interest_rate', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="payment_amount">Payment Amount</Label>
            <Input
              id="payment_amount"
              type="number"
              step="0.01"
              value={formData.payment_amount || ''}
              onChange={(e) => onChange('payment_amount', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="original_term_months">Term (Months)</Label>
            <Input
              id="original_term_months"
              type="number"
              value={formData.original_term_months || ''}
              onChange={(e) => onChange('original_term_months', parseInt(e.target.value) || undefined)}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Balloon Payment */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <Switch
            id="has_balloon_payment"
            checked={formData.has_balloon_payment}
            onCheckedChange={(checked) => onChange('has_balloon_payment', checked)}
          />
          <Label htmlFor="has_balloon_payment" className="font-semibold">Balloon Payment</Label>
        </div>
        {formData.has_balloon_payment && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="balloon_amount">Balloon Amount</Label>
              <Input
                id="balloon_amount"
                type="number"
                step="0.01"
                value={formData.balloon_amount || ''}
                onChange={(e) => onChange('balloon_amount', parseFloat(e.target.value) || undefined)}
              />
            </div>
            <div>
              <Label htmlFor="balloon_due_date">Due Date</Label>
              <Input
                id="balloon_due_date"
                type="date"
                value={formData.balloon_due_date || ''}
                onChange={(e) => onChange('balloon_due_date', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Payment Tracking */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Payment Tracking</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first_payment_to_me">First Payment to Me</Label>
            <Input
              id="first_payment_to_me"
              type="date"
              value={formData.first_payment_to_me || ''}
              onChange={(e) => onChange('first_payment_to_me', e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Last payment to seller is the month before this date
            </p>
          </div>
          <div>
            <Label>Payments Made Before Takeover</Label>
            <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center justify-between">
              <span className="font-medium">{paymentsMade} payments</span>
              {formData.first_payment_date && formData.first_payment_to_me && (
                <span className="text-xs text-muted-foreground">
                  ({parseISO(formData.first_payment_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {parseISO(formData.first_payment_to_me).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Months between first payment and your first payment
            </p>
          </div>
        </div>
      </div>

      <Separator />

      {/* Current Status & Valuation */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Current Status & Valuation</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="current_balance">Current Balance</Label>
            <CurrencyInput
              id="current_balance"
              value={formData.current_balance}
              onChange={handleBalanceChange}
            />
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-muted-foreground">
                {manualBalanceOverride ? 'Manually entered' : 'Auto-calculated'}
              </p>
              {manualBalanceOverride && (
                <button
                  type="button"
                  onClick={handleRecalculateBalance}
                  className="text-xs text-primary hover:underline"
                >
                  Recalculate
                </button>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="payments_remaining">Payments Remaining</Label>
            <Input
              id="payments_remaining"
              type="number"
              value={formData.payments_remaining || ''}
              onChange={(e) => onChange('payments_remaining', parseInt(e.target.value) || undefined)}
            />
            <p className="text-xs text-muted-foreground mt-1">Manually enter buyer's remaining payments</p>
          </div>
          <div>
            <Label htmlFor="estimated_property_value">Property Value</Label>
            <Input
              id="estimated_property_value"
              type="number"
              step="0.01"
              value={formData.estimated_property_value || ''}
              onChange={(e) => onChange('estimated_property_value', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="offer_price">Offer Price</Label>
            <Input
              id="offer_price"
              type="number"
              step="0.01"
              value={formData.offer_price || ''}
              onChange={(e) => onChange('offer_price', parseFloat(e.target.value) || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="offer_date">Offer Date</Label>
            <Input
              id="offer_date"
              type="date"
              value={formData.offer_date || ''}
              onChange={(e) => onChange('offer_date', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="investor_quote">Investor Quote</Label>
            <Input
              id="investor_quote"
              type="number"
              step="0.01"
              value={formData.investor_quote || ''}
              onChange={(e) => onChange('investor_quote', parseFloat(e.target.value) || undefined)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
